import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Breadcrumb from 'Components/UI/Breadcrumb';
import Select from "react-select";
import { useNavigate } from 'react-router-dom';
import { getAllBookings } from 'helpers/API Calls/Operations/booking';
import { BookingData } from 'types/booking';
import { addTestimonials } from 'helpers/API Calls/System & Settings/Customar Forms';

interface TestimonialFormValues {
  name: string;
  email: string;
  date_of_event: string;
  booking_id: number | null;
  rating: string;
  comments: string;
}

interface DropdownOption {
  value: number;
  label: string;
  name?: string;
  email?: string;
  date_of_event?: string;
}

const RATING_OPTIONS = [
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Very Good', label: 'Very Good' },
  { value: 'Mostly Good', label: 'Mostly Good' },
  { value: 'Reasonable', label: 'Reasonable' },
  { value: 'Poor', label: 'Poor' }
];  

function AddTestimonials() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dropdownOptions, setDropdownOptions] = useState<{
    bookings: DropdownOption[];
  }>({
    bookings: []
  });

  useEffect(() => {
    const fetchData = async () => {
        setIsLoading(true);
        const bookings = await getAllBookings();
        if(bookings.success) {
            const bookingsOptions = bookings.data?.items?.map((bookings: BookingData) => ({
                value: bookings.id,
                label: `Booking #${bookings.id} - ${bookings.customer?.name}`,
                name: bookings.customer?.name,
                email: bookings.customer?.email, 
                date_of_event: bookings.booking_date
            }));
            setDropdownOptions(prev => ({ ...prev, bookings: bookingsOptions }));
        }
        setIsLoading(false);
    }
    fetchData(); 
  }, [])

  const formik = useFormik<TestimonialFormValues>({    
    initialValues: {
      name: '',
      email: '',
      date_of_event: '',
      booking_id: null,
      rating: '',
      comments: '',
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('Name is required'),
      email: yup.string().email('Invalid email').required('Email is required'),
      date_of_event: yup.string(),
      booking_id: yup.number().nullable(),
      rating: yup.string().required('Rating is required'),
      comments: yup.string().max(1000, 'Comments cannot exceed 1000 characters'),
    }),    
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const testimonialData = {
        name: values.name,
        email: values.email,
        date_of_event: values.date_of_event,
        booking_id: values.booking_id,
        rating: values.rating,
        comments: values.comments,
        // status: 1,
      };
      const response = await addTestimonials(testimonialData);
      if (response.success) {
        navigate('/pages/testimonials');
      }
      setSubmitting(false);
    },
  });
  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Testimonials", link: "/pages/testimonials" }, {label: 'Create'}]} />
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Basic Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Testimonial Information</CardTitle>
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="booking_id">Booking</Label>
                      <Select
                        id="booking_id"
                        name="booking_id"
                        placeholder="Select Booking"
                        options={dropdownOptions.bookings}
                        value={dropdownOptions.bookings.find(option => option.value === formik.values.booking_id)}
                        onChange={(option) => {
                            formik.setFieldValue("booking_id", option?.value); 
                            formik.setFieldValue("name", option?.name || '');
                            formik.setFieldValue("email", option?.email || '');
                            formik.setFieldValue("date_of_event", option?.date_of_event || '');
                        }}
                        onBlur={formik.handleBlur}
                        className={
                            formik.touched.booking_id && formik.errors.booking_id
                            ? "is-invalid"
                            : ""
                        }
                        isLoading={isLoading}
                      />
                      {formik.errors.booking_id && formik.touched.booking_id && (
                        <div className="invalid-feedback d-block">{formik.errors.booking_id}</div>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="date_of_event">Event Date</Label>
                      <Input
                        id="date_of_event"
                        name="date_of_event"
                        type="date"
                        value={formik.values.date_of_event}
                        onChange={formik.handleChange}
                        invalid={formik.touched.date_of_event && formik.errors.date_of_event ? true : false}
                      />
                      {formik.errors.date_of_event && formik.touched.date_of_event && (
                        <FormFeedback type="invalid">{formik.errors.date_of_event}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="name">Name <span className="text-danger">*</span></Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter customer name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        invalid={formik.touched.name && formik.errors.name ? true : false}
                      />
                      {formik.errors.name && formik.touched.name && (
                        <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="email">Email <span className="text-danger">*</span></Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter customer email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        invalid={formik.touched.email && formik.errors.email ? true : false}
                      />
                      {formik.errors.email && formik.touched.email && (
                        <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="rating">Rating <span className="text-danger">*</span></Label>
                      <Select
                        id="rating"
                        name="rating"
                        placeholder="Select Rating"
                        options={RATING_OPTIONS}
                        value={RATING_OPTIONS.find(option => option.value === formik.values.rating)}
                        onChange={(option) => formik.setFieldValue("rating", option?.value)}
                        onBlur={formik.handleBlur}
                        className={
                            formik.touched.rating && formik.errors.rating
                            ? "is-invalid"
                            : ""
                        }
                      />
                      {formik.errors.rating && formik.touched.rating && (
                        <div className="invalid-feedback d-block">{formik.errors.rating}</div>
                      )}
                    </div>
                  </Col>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="comments">Comments</Label>
                      <Input
                        id="comments"
                        name="comments"
                        type="textarea"
                        rows="4"
                        placeholder="Enter testimonial comments"
                        value={formik.values.comments}
                        onChange={formik.handleChange}
                        invalid={formik.touched.comments && formik.errors.comments ? true : false}
                      />
                      {formik.errors.comments && formik.touched.comments && (
                        <FormFeedback type="invalid">{formik.errors.comments}</FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="d-flex justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Submitting...' : 'Submit Testimonial'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}

export default AddTestimonials;
